import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Shield, Check, X, Loader2, AlertCircle, Clock } from 'lucide-react';

const SupportAccessPage = () => {
  const { action, token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const processAction = async () => {
    if (!user) {
      toast.error('Please log in to respond to this request');
      navigate(`/login?redirect=/support/${action}/${token}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current user's auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      // Call the Edge Function to process the support access request
      const { data, error: funcError } = await supabase.functions.invoke(
        action === 'grant' ? 'grant-support-access' : 'deny-support-access',
        {
          body: { token }
        }
      );

      if (funcError) {
        // If Edge Function doesn't exist, update directly in database
        const { data: request, error: fetchError } = await supabase
          .from('support_access_requests')
          .select('*')
          .eq('access_token', token)
          .single();

        if (fetchError || !request) {
          throw new Error('Request not found or expired');
        }

        // Update request status
        const newStatus = action === 'grant' ? 'granted' : 'denied';
        const { error: updateError } = await supabase
          .from('support_access_requests')
          .update({ 
            status: newStatus,
            responded_at: new Date().toISOString()
          })
          .eq('access_token', token);

        if (updateError) throw updateError;

        setResult({
          message: `Support access ${newStatus}`,
          expires_at: action === 'grant' ? request.expires_at : null
        });
      } else {
        setResult(data);
      }
      
      if (action === 'grant') {
        toast.success('Support access granted successfully');
      } else {
        toast.success('Support access denied');
      }
    } catch (err) {
      console.error('Error processing request:', err);
      setError(err.message || 'Failed to process request');
      toast.error(err.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && token && (action === 'grant' || action === 'deny')) {
      processAction();
    }
  }, [user, token, action]);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-[#003B5C] mb-4">Authentication Required</h1>
          <p className="text-slate-600 mb-6">
            Please log in to your Lucent Sky ESG account to respond to this support access request.
          </p>
          <Button
            onClick={() => navigate(`/login?redirect=/support/${action}/${token}`)}
            className="bg-[#003B5C] hover:bg-[#002A47] text-white w-full"
          >
            Log In to Continue
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-[#003B5C] animate-spin mx-auto mb-6" />
          <h1 className="text-xl font-bold text-[#003B5C] mb-2">Processing Request</h1>
          <p className="text-slate-600">
            {action === 'grant' ? 'Granting support access...' : 'Denying support access...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-[#003B5C] mb-4">Request Failed</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#003B5C] hover:bg-[#002A47] text-white"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="support-access-response">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
            action === 'grant' ? 'bg-[#74A57F]/20' : 'bg-slate-100'
          }`}>
            {action === 'grant' ? (
              <Check className="w-8 h-8 text-[#74A57F]" />
            ) : (
              <X className="w-8 h-8 text-slate-500" />
            )}
          </div>
          
          <h1 className="text-xl font-bold text-[#003B5C] mb-4">
            {action === 'grant' ? 'Access Granted!' : 'Access Denied'}
          </h1>
          
          <p className="text-slate-600 mb-6">
            {action === 'grant' ? (
              <>
                Support access has been granted. The support agent can now view your organisation's data to help resolve your issue.
                <br /><br />
                <span className="text-sm text-slate-500 flex items-center justify-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Access expires: {result.expires_at ? new Date(result.expires_at).toLocaleString('en-AU') : 'N/A'}
                </span>
              </>
            ) : (
              'The support access request has been denied. The support agent will not be able to access your data.'
            )}
          </p>
          
          <p className="text-xs text-slate-400 mb-6">
            All support actions are logged in your audit trail for transparency.
          </p>
          
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#003B5C] hover:bg-[#002A47] text-white w-full"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-[#003B5C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Shield className="w-8 h-8 text-[#003B5C]" />
        </div>
        <h1 className="text-xl font-bold text-[#003B5C] mb-4">Support Access Request</h1>
        <p className="text-slate-600 mb-6">
          Would you like to {action === 'grant' ? 'grant' : 'deny'} support access to your organisation?
        </p>
        <div className="flex space-x-4">
          <Button
            onClick={processAction}
            disabled={loading}
            className={`flex-1 ${
              action === 'grant' 
                ? 'bg-[#74A57F] hover:bg-[#5F8575]' 
                : 'bg-slate-500 hover:bg-slate-600'
            } text-white`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              action === 'grant' ? 'Grant Access' : 'Deny Access'
            )}
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupportAccessPage;
